import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  TextInput,
  SelectMenu,
  BodyText,
  Checkbox,
  showToast,
} from '@spotoninc/nexus-react';
import { useNexus } from '@spotoninc/nexus-react';
import { SelectPicker } from 'rsuite';
import '../../../styles/reskinned-integration.css';
import { IntegrationApiService } from './integrationApiService';
import { trackIntegrationAdded } from '../../../utils/analytics/integrationTracking';

/**
 * Reskinned Add Integration Modal Component based on Nexus design system
 * This component handles adding a new integration with form validation and API integration
 * It is only rendered when the showReskin feature flag is true
 */
const ReskinnedAddIntegrationModal = ({
  isOpen,
  onClose,
  onSave, // This will be called after successful integration creation
  formState,
  setFormState,
  country,
  integrationTypes,
  periodTypes,
  defaultPeriod,
  cashAccData,
  allIntegrationTypes,
  companySwitcherActive,
  apps, // Array of existing integrations (kept for backward compatibility with tests)
  isFirstIntegration, // Helper function to check if this is the first integration (considers connection status)
}) => {
  const { theme } = useNexus();
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  // We don't need the ref anymore since we're using state
  // const appNameRef = useRef(null);
  const [appNameValue, setAppNameValue] = useState('');

  // Destructure formState with default values
  const {
    captureLocation = false,
    locationAttr = false,
    periodCalTypeFlag = false,
    locationAttrDisplay = false,
    integration_type = '',
    countrySelected = '',
    periodCalType = defaultPeriod,
    cashAc = '',
    tpc = false,
  } = formState;

  // Check form validity whenever required fields change
  useEffect(() => {
    // Check if all required fields have values
    const isValid =
      appNameValue.trim() !== '' &&
      countrySelected !== '' &&
      integration_type !== '' &&
      cashAc !== '';

    setIsFormValid(isValid);
  }, [appNameValue, countrySelected, integration_type, cashAc]);

  // Format data for Nexus SelectMenu and rsuite SelectPicker
  const formatSelectData = data => {
    // Check if data is undefined or null
    if (!data) {
      return [];
    }

    // Make sure data is an array
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(item => ({
      value: item.value,
      label: item.label,
    }));
  };

  const handleIntegrationTypeChange = value => {
    // Check if allIntegrationTypes is undefined or null
    if (!allIntegrationTypes) {
      setFormState({
        ...formState,
        integration_type: value,
      });
      return;
    }

    // Find the selected integration type
    const selectedIntegrationType = allIntegrationTypes.find(
      item => item.integration_type === value
    );

    // Update individual state properties
    if (selectedIntegrationType) {
      // First update the integration_type
      setFormState({
        ...formState,
        integration_type: value,
        tpc: selectedIntegrationType.tpc,
        captureLocationDisplay: selectedIntegrationType.capture_location,
        locationAttrDisplay: selectedIntegrationType.location_attr,
        hotglueFlowId: selectedIntegrationType.flow_id,
        sourceId: selectedIntegrationType.source_id,
      });
    } else {
      // If no matching integration type is found, just update the integration_type
      setFormState({
        ...formState,
        integration_type: value,
      });
    }
  };

  /**
   * Handle saving the integration
   * This function validates the form data and sends it to the API
   */
  const handleSave = async () => {
    // Set loading state
    setIsLoading(true);

    // We already validate the form with the disabled button,
    // but let's double-check to be safe
    if (!isFormValid) {
      showToast({
        priority: 'error',
        content: 'Please fill in all required fields',
        autoClose: 5000,
      });
      setIsLoading(false);
      return;
    }

    // All validation is now handled by the isFormValid check above
    // Create form data
    const formData = new FormData();
    formData.append('app_name', appNameValue);
    formData.append('integration_type', integration_type);
    formData.append('capture_location', captureLocation ? 'true' : 'false');
    formData.append('location_attr', locationAttr ? 'true' : 'false');
    formData.append('country', countrySelected);
    formData.append('period_cal', periodCalTypeFlag ? 'true' : 'false');
    formData.append('period_cal_type', periodCalType || defaultPeriod);
    formData.append('cashAc', cashAc);

    try {
      // Use the API service to add the integration
      const response = await IntegrationApiService.addIntegration(formData);

      // Check if the response contains an error message
      if (response && response.error) {
        throw new Error(response.error || 'Unknown error occurred');
      }

      // Track integration added event
      // Use the helper function to check if this is the first integration
      // This considers both the number of integrations and their connection status
      const isFirst = isFirstIntegration ? isFirstIntegration() : false;
      trackIntegrationAdded({
        integrationId: response.last_integration_id || '',
        integrationType: integration_type,
        appName: appNameValue,
        isFirstIntegration: isFirst,
      });

      // Show success message
      showToast({
        priority: 'success',
        content: 'Integration added successfully',
        autoClose: 5000,
      });

      // Call the parent's onSave function to handle any additional logic
      if (onSave) {
        onSave(response);
      }

      // Close the modal after successful save
      // The loading state will remain active until the modal is closed
      onClose();
    } catch (error) {
      console.error('Error adding integration:', error);
      showToast({
        priority: 'error',
        content: `Error adding integration: ${error.message || 'Unknown error'}`,
        autoClose: 5000,
      });

      // Only reset loading state if there was an error
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Integration"
      className={theme.className}
    >
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* App Name Input */}
          <div>
            <BodyText emphasis style={{ marginBottom: '8px' }}>
              App Name
            </BodyText>
            <TextInput
              id="appName"
              placeholder="Enter App Name"
              value={appNameValue}
              onChange={e => {
                const value = e.target.value;
                setAppNameValue(value);
                setFormState({ ...formState, appName: value });
              }}
            />
          </div>

          {/* Country Selector */}
          <div>
            <BodyText emphasis style={{ marginBottom: '8px' }}>
              Country
            </BodyText>
            <SelectPicker
              className="reskin-country-selector"
              data={formatSelectData(country)}
              placeholder="Select Country"
              searchable={true}
              style={{ width: '100%', height: '46px' }}
              menuStyle={{
                zIndex: 9999,
                maxHeight: '300px',
                width: '300px',
                backgroundColor: 'white',
              }}
              value={countrySelected}
              onChange={value => {
                setFormState({
                  ...formState,
                  countrySelected: value,
                });
              }}
              container={() => document.body}
            />
          </div>
          {/* Integration Type Selector */}
          <div>
            <BodyText emphasis style={{ marginBottom: '8px' }}>
              Integration Type
            </BodyText>
            <SelectMenu
              options={formatSelectData(integrationTypes)}
              placeholder="Select Integration Type"
              onChange={(_, value) => handleIntegrationTypeChange(value)}
            />
          </div>
          {/* Cash or Accrual Selector */}
          <div>
            <BodyText emphasis style={{ marginBottom: '8px' }}>
              Accounting Type
            </BodyText>
            <SelectMenu
              options={formatSelectData(cashAccData)}
              placeholder="Select Cash or Accrual"
              value={cashAc}
              onChange={(_, value) =>
                setFormState({ ...formState, cashAc: value })
              }
            />
          </div>

          {/* Period Types - Checkbox with conditional dropdown */}
          {companySwitcherActive && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Checkbox
                id="periodTypes"
                checked={periodCalTypeFlag}
                onChange={() => {
                  setFormState({
                    ...formState,
                    periodCalTypeFlag: !periodCalTypeFlag,
                    periodCalType: defaultPeriod,
                  });
                }}
                label="Period Types"
              />
              {periodCalTypeFlag && (
                <div style={{ marginLeft: '28px' }}>
                  <BodyText emphasis style={{ marginBottom: '8px' }}>
                    Select Period Type
                  </BodyText>
                  <SelectMenu
                    options={formatSelectData(periodTypes)}
                    placeholder={defaultPeriod}
                    defaultValue={defaultPeriod}
                    onChange={(_, value) =>
                      setFormState({ ...formState, periodCalType: value })
                    }
                  />
                </div>
              )}
            </div>
          )}

          {/* Location Setup - Checkbox with conditional dropdown */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Checkbox
              id="locationSetup"
              checked={captureLocation || locationAttr}
              onChange={() => {
                const isCurrentlyChecked = captureLocation || locationAttr;
                if (isCurrentlyChecked) {
                  // Uncheck - reset to "Neither"
                  setFormState({
                    ...formState,
                    locationAttr: false,
                    attributeType: false,
                    captureLocation: false,
                  });
                } else {
                  // Check - default to "Class"
                  setFormState({
                    ...formState,
                    locationAttr: false,
                    attributeType: false,
                    captureLocation: true,
                  });
                }
              }}
              label="Location Setup"
            />
            {(captureLocation || locationAttr) && (
              <div style={{ marginLeft: '28px' }}>
                <BodyText emphasis style={{ marginBottom: '8px' }}>
                  Multi-Unit Setup
                </BodyText>
                <SelectMenu
                  options={[
                    { value: 'class', label: 'Class' },
                    { value: 'location', label: 'Location' },
                  ]}
                  value={locationAttr ? 'location' : 'class'}
                  onChange={(_, value) => {
                    if (value === 'class') {
                      setFormState({
                        ...formState,
                        locationAttr: false,
                        attributeType: false,
                        captureLocation: true,
                      });
                    } else if (value === 'location') {
                      setFormState({
                        ...formState,
                        locationAttr: true,
                        attributeType: true,
                        captureLocation: true,
                      });
                    }
                  }}
                  disabled={!locationAttrDisplay}
                  style={{ width: '100%' }}
                />
                <BodyText
                  size="small"
                  style={{ marginTop: '8px', color: '#666' }}
                >
                  Unsure? Choose “Neither.” You can update this later.
                </BodyText>
              </div>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="tonal" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          isLoading={isLoading}
          disabled={!isFormValid}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReskinnedAddIntegrationModal;
